import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;
  #mobile = matchMedia('(max-width: 749px)');
  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  #abortController;
  #loopSlide;
  #scrollTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const options = { signal: this.#abortController.signal };
    this.addEventListener('pointerenter', this.suspend, options);
    this.addEventListener('pointerleave', this.resume, options);
    this.addEventListener('focusin', this.suspend, options);
    this.addEventListener('focusout', this.resume, options);
    this.addEventListener('pointerdown', this.suspend, options);
    this.addEventListener('pointerup', this.resume, options);
    this.addEventListener('pointercancel', this.resume, options);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, options);
    this.#mobile.addEventListener('change', this.#resetPlayback, options);
    this.#reducedMotion.addEventListener('change', this.#resetPlayback, options);

    this.querySelector('.announcement-bar__slides')?.addEventListener('scroll', this.#handleLoopScroll, options);
    this.#setupLoop();
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.#abortController?.abort();
    clearTimeout(this.#scrollTimer);
    this.#loopSlide?.remove();
    this.#loopSlide = undefined;
  }

  #resetPlayback = () => {
    this.suspend();
    this.#setupLoop();
    if (!this.paused) this.play();
  };

  #setupLoop() {
    clearTimeout(this.#scrollTimer);
    this.#loopSlide?.remove();
    this.#loopSlide = undefined;
    const slides = this.refs.slides ?? [];
    if (!this.hasAttribute('data-mobile-scroll') || !this.#mobile.matches || slides.length < 2) return;
    // A visual copy lets the last announcement advance in the same direction.
    const copy = slides[0].cloneNode(true);
    for (const element of [copy, ...copy.querySelectorAll('*')]) {
      element.removeAttribute('id');
      element.removeAttribute('ref');
      element.removeAttribute('data-shopify-editor-block');
    }
    copy.setAttribute('aria-hidden', 'true');
    copy.style.opacity = '1';
    copy.style.visibility = 'visible';
    copy.querySelectorAll('a, button, [tabindex]').forEach((element) => element.setAttribute('tabindex', '-1'));
    this.#loopSlide = copy;
    slides[0].parentElement.append(copy);
  }

  #handleLoopScroll = () => {
    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(() => {
      const track = this.querySelector('.announcement-bar__slides');
      if (!track || !this.#loopSlide) return;
      if (Math.abs(this.#loopSlide.getBoundingClientRect().left - track.getBoundingClientRect().left) > 1) return;
      // Both positions look identical; reset only after the forward scroll settles.
      track.scrollBy({ left: this.refs.slides[0].getBoundingClientRect().left - this.#loopSlide.getBoundingClientRect().left, behavior: 'instant' });
    }, 150);
  };

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  togglePlayback() {
    if (this.paused) this.play();
    else this.pause();
    const button = this.querySelector('.announcement-bar__playback');
    button?.setAttribute('aria-label', (this.paused ? this.dataset.playLabel : this.dataset.pauseLabel) ?? '');
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in milliseconds between slides.
   */
  play(interval = this.autoplayInterval) {
    this.suspend();
    if (!this.autoplay || this.#reducedMotion.matches || (this.hasAttribute('data-mobile-scroll') && !this.#mobile.matches)) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if ((matchMedia('(hover: hover)').matches && this.matches(':hover')) || this.matches(':focus-within') || document.hidden) return;

      if (this.hasAttribute('data-mobile-scroll')) {
        const track = this.querySelector('.announcement-bar__slides');
        const slides = this.refs.slides ?? [];
        if (!track || slides.length < 2) return;
        const nearest = slides.reduce((best, slide, index) =>
          Math.abs(slide.getBoundingClientRect().left - track.getBoundingClientRect().left) <
          Math.abs(slides[best].getBoundingClientRect().left - track.getBoundingClientRect().left) ? index : best, 0);
        const next = slides[nearest + 1] ?? this.#loopSlide ?? slides[0];
        track.scrollBy({ left: next.getBoundingClientRect().left - track.getBoundingClientRect().left, behavior: 'smooth' });
        return;
      }

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
